import type { ComputedRef, Ref } from 'vue';
import type { DetailPageContext } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/types/form-block';
import type { AnyItem, CompositePrimaryKey, GlobalCollectionId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { FormSaveError, FormSaveHandler, RStoreFormObject } from '#layers/forms/public';
import { tryOnScopeDispose } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from '#app/composables/router';
import { getItemPrimaryKey, isSameKey } from '#layers/data-model/public';
import { useFormSubmitHandler } from '#layers/forms/public';
import { useCollectionRoutes } from '#layers/workspace/public';

export interface UseFormBlockSubmitParams {
	formObject: Ref<RStoreFormObject<AnyItem> | null>;
	isCreate: ComputedRef<boolean>;
	itemId: ComputedRef<CompositePrimaryKey | null>;
	finalCollectionId: ComputedRef<GlobalCollectionId | undefined>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	isDetailPageForm: ComputedRef<boolean>;
	detailPageContext: Pick<DetailPageContext, 'formMode'>;
	captureSaveHandlers: () => readonly FormSaveHandler[];
	saveHandlerErrors: Ref<FormSaveError[]>;
	clearSaveHandlerErrors: () => void;
	emitOnRootBlock: (event: string, payload: FormBlockSuccessEventPayload) => void;
	onSuccess?: (payload: FormBlockSuccessEventPayload) => void;
	onSubmit?: (data: { data: AnyItem }) => void;
	hasExternalChanges: () => boolean;
	runOperation?: <T>(operation: () => Promise<T>) => Promise<T>;
}

export interface UseFormBlockSubmitReturn {
	onSubmit: () => Promise<void>;
	/**
	 * Form-only submit disabled state.
	 * Does NOT consider external changes - the master composable handles that.
	 * Returns true when form has no changes (in update mode).
	 */
	formOnlySubmitDisabled: ComputedRef<boolean>;
	submitError: Ref<Error | null>;
	submitted: Ref<boolean>;
}

/**
 * Handles form submission logic for form blocks.
 * Supports both submit mode (persists to backend) and emit mode (just emits data).
 */
export function useFormBlockSubmit(params: UseFormBlockSubmitParams): UseFormBlockSubmitReturn {
	const {
		formObject,
		isCreate,
		itemId,
		finalCollectionId,
		collectionData,
		isDetailPageForm,
		detailPageContext,
		captureSaveHandlers,
		saveHandlerErrors,
		clearSaveHandlerErrors,
		emitOnRootBlock,
		onSuccess,
		onSubmit: onSubmitCallback,
		hasExternalChanges,
		runOperation = (operation) => operation(),
	} = params;

	const submitted = ref(false);
	let submittedSaveHandlers: readonly FormSaveHandler[] = [];
	let submittedCollection: ResolvedMonospaceCollection | undefined;
	let submissionInFlight = false;
	let active = true;

	tryOnScopeDispose(() => {
		active = false;
	});

	const router = useRouter();
	const { detailRoute } = useCollectionRoutes(collectionData);

	const { onSubmit: handleSubmit, submitDisabled: formOnlySubmitDisabled, submitError } = useFormSubmitHandler<AnyItem>(formObject, {
		isCreate,
		onBeforeSubmit: () => {
			submitted.value = false;
			clearSaveHandlerErrors();
		},
		onSuccess: async (result) => {
			if (!submittedCollection) {
				throw new Error('Collection data unavailable during form success handling');
			}

			const itemKey = getItemPrimaryKey(result, submittedCollection);

			const handlerResults = await runSaveHandlers(submittedSaveHandlers, itemKey);
			if (!active) return;

			// Collect all errors from handlers
			saveHandlerErrors.value = handlerResults
				.flat()
				.filter((error): error is FormSaveError => !!error);

			submitted.value = true;

			const successPayload: FormBlockSuccessEventPayload = {
				key: itemKey,
				data: result,
			};

			onSuccess?.(successPayload);

			if (isDetailPageForm.value) {
				emitOnRootBlock('success', successPayload);
			}

			// Redirect after creating or changing the primary key. Create and edit share the same
			// page intent, so use the item's original key rather than the page intent.
			const shouldRedirectToDetail = isDetailPageForm.value
				&& (isCreate.value || (itemId.value !== null && !isSameKey(itemId.value, itemKey)));

			if (shouldRedirectToDetail) {
				const route = detailRoute(itemKey);

				if (route) {
					router.push(route);
				}
			}
		},
	});

	async function triggerSaveHandlersOnly(
		saveHandlers: readonly FormSaveHandler[],
		collection: ResolvedMonospaceCollection | undefined,
	) {
		if (!formObject.value || !collection) return;

		submitted.value = false;
		clearSaveHandlerErrors();

		const itemKey = getItemPrimaryKey(formObject.value, collection);
		const handlerResults = await runSaveHandlers(saveHandlers, itemKey);
		if (!active) return;

		saveHandlerErrors.value = handlerResults
			.flat()
			.filter((error): error is FormSaveError => !!error);

		submitted.value = true;

		const successPayload: FormBlockSuccessEventPayload = {
			key: itemKey,
			data: formObject.value?.$getRawData({ clone: true }) ?? {},
		};

		onSuccess?.(successPayload);

		if (isDetailPageForm.value) {
			emitOnRootBlock('success', successPayload);
		}
	}

	async function onSubmit() {
		if (submissionInFlight) return;
		submissionInFlight = true;
		submittedCollection = collectionData.value;

		return runOperation(async () => {
			try {
				// Emit submit callback before mode-specific logic
				if (onSubmitCallback) {
					onSubmitCallback({ data: formObject.value?.$getRawData({ clone: true }) ?? {} });
				}

				if (!finalCollectionId.value) {
					submitted.value = false;
					clearSaveHandlerErrors();
					submitted.value = true;
					submitError.value = null;
					return;
				}

				if (!detailPageContext.formMode || detailPageContext.formMode === 'submit') {
					if (!formOnlySubmitDisabled.value) {
						submittedSaveHandlers = captureSaveHandlers();
						await handleSubmit();
					}
					else if (hasExternalChanges()) {
						// Form has no field changes, but external save handlers (e.g. policy assignment) may need to persist
						await triggerSaveHandlersOnly(captureSaveHandlers(), submittedCollection);
					}
				}
				else {
					// Non-submit mode (emit mode) - just emit success without persisting
					// Reset state before submission (matches submit mode behavior)
					submitted.value = false;
					clearSaveHandlerErrors();

					try {
						if (!submittedCollection) {
							throw new Error('Collection data unavailable during form emit handling');
						}

						const clonedData = formObject.value?.$getRawData({ clone: true }) ?? {};

						const payload: FormBlockSuccessEventPayload = {
							key: getItemPrimaryKey(clonedData, submittedCollection),
							data: clonedData,
						};

						submitError.value = null;
						submitted.value = true;

						onSuccess?.(payload);

						if (isDetailPageForm.value) {
							emitOnRootBlock('success', payload);
						}
					}
					catch (error) {
						submitError.value = error instanceof Error ? error : new Error(String(error));
					}
				}
			}
			finally {
				submittedSaveHandlers = [];
				submittedCollection = undefined;
				submissionInFlight = false;
			}
		});
	}

	return {
		onSubmit,
		formOnlySubmitDisabled,
		submitError,
		submitted,
	};
}

function runSaveHandlers(
	handlers: readonly FormSaveHandler[],
	itemKey: CompositePrimaryKey,
): Promise<FormSaveError[][]> {
	return Promise.all(handlers.map((handler) => handler({ itemKey })));
}
